import { NodoDto } from "@/api";
import Button from "@/elements/Button";
import { withOpeningReset } from "@/utils/withOpeningReset";
import {
  Box,
  DialogActions,
  DialogTitle,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { GattiQrCode } from "./GattiQrCode";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NodoTipoLabel } from "@/utils/nodoUtils";
import { portaleHelpers } from "@/utils/portaleUtils";

function QRCodeDialog({
  open = false,
  onClose,
  nodo,
}: {
  open?: boolean;
  onClose?: () => void;
  nodo?: NodoDto;
}) {
  const { t } = useTranslation();
  const [portaleId, setPortaleId] = useState<number | null>(null);

  useEffect(() => {
    if (nodo?.portali && nodo.portali.length === 1) {
      setPortaleId(nodo.portali[0].portaleId);
    } else {
      setPortaleId(null);
    }
  }, [nodo]);

  const nodoPortale = useMemo(() => {
    if (!nodo?.portali || !portaleId) {
      return null;
    }

    return nodo.portali.find((np) => np.portaleId === portaleId);
  }, [portaleId, nodo]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{t("qrCodeDialog.titolo")}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2}>
          {nodo?.portali && nodo.portali.length > 1 && (
            <Box display="flex" flexDirection="row" gap={1} alignItems="center">
              <Typography variant="body2">
                {t("qrCodeDialog.selezionaPortale")}
              </Typography>
              <Box>
                <Select
                  style={{ minWidth: 120 }}
                  value={portaleId || ""}
                  onChange={(e) => {
                    if (e.target.value) {
                      setPortaleId(parseInt(`${e.target.value}`));
                    } else {
                      setPortaleId(null);
                    }
                  }}
                >
                  {nodo?.portali?.map((p) => {
                    return (
                      <MenuItem key={p.portaleId} value={p.portaleId}>
                        {portaleHelpers[p.portaleId].portaleNome}
                      </MenuItem>
                    );
                  })}
                </Select>
              </Box>
            </Box>
          )}

          {nodoPortale && (
            <Box
              display={"grid"}
              gridTemplateColumns={
                nodo?.haAlloggiamentiFiltro ? "auto 1fr" : "auto"
              }
              gap={4}
            >
              <Box display={"flex"} flexDirection={"column"} gap={2}>
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  alignItems={"start"}
                >
                  <Typography variant="subtitle2" ml={1}>
                    {t("qrCodeDialog.qrNodo", {
                      tipo: nodo && NodoTipoLabel[nodo.tipo]?.label(t),
                    })}
                  </Typography>
                </Box>
                <GattiQrCode
                  value={`https://${
                    portaleHelpers[nodoPortale.portaleId].qrCodeHost
                  }/qr/centrale/${nodoPortale.tokenCodiceQR}`}
                  size={240}
                  fileName={t("qrCodeDialog.fileNamePosizione", {
                    nome: nodo?.nome,
                  })}
                />
              </Box>

              {nodo?.haAlloggiamentiFiltro && (
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  gap={2}
                  maxHeight={"400px"}
                  overflow={"auto"}
                >
                  <Typography variant="subtitle2">
                    {t("qrCodeDialog.qrAlloggiamenti")}
                  </Typography>

                  {nodo?.alloggiamentiFiltro?.length === 0 && (
                    <Typography fontStyle="italic" variant="body2">
                      {t("qrCodeDialog.nessunAlloggiamento")}
                    </Typography>
                  )}

                  {nodo?.alloggiamentiFiltro?.map((af, i) => {
                    const host =
                      portaleHelpers[nodoPortale.portaleId].qrCodeHost;
                    const token = af?.codiciQr?.find(
                      (x) => x.portaleId === nodoPortale.portaleId
                    )?.tokenCodiceQR;
                    const url = `https://${host}/qr/filtro/${token}`;

                    return (
                      <Box
                        key={i}
                        display={"flex"}
                        alignItems={"start"}
                        flexDirection={"column"}
                        gap={2}
                        sx={{
                          border: "1px solid gray",
                          borderRadius: "4px",
                          p: 1,
                        }}
                      >
                        <Typography variant="body2">{af.nome}</Typography>
                        <GattiQrCode
                          fileName={t("qrCodeDialog.fileNameFiltro", {
                            nome: af.nome,
                          })}
                          value={url}
                          size={160}
                        />
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="light" onClick={onClose}>
          {t("azioni.chiudi")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default withOpeningReset(QRCodeDialog);
